import { useState, useEffect, useRef } from 'react';
import { useAwsCredentialsStatus } from 'pages/Integrations/api/awsCredentialsStatus';
import { useQueryClient } from '@tanstack/react-query';

const MAX_POLLING_TIME = 900000; // 15 minutes
const POLLING_INTERVAL = 20000; // 20 seconds

// Constants for localStorage keys
const POLLING_STATE_KEY = 'aws-credentials-polling';
const POLLING_START_TIME_KEY = 'aws-credentials-polling-start-time';

// localStorage utility functions
const storageUtils = {
  getBoolean: (key: string, defaultValue = false): boolean => {
    try {
      return localStorage.getItem(key) === 'true';
    } catch (e) {
      return defaultValue;
    }
  },

  setBoolean: (key: string, value: boolean): void => {
    try {
      localStorage.setItem(key, value ? 'true' : 'false');
    } catch (e) {
      console.error(`Failed to store ${key}:`, e);
    }
  },

  getNumber: (key: string): number | null => {
    try {
      const value = localStorage.getItem(key);
      return value ? parseInt(value, 10) : null;
    } catch (e) {
      console.error(`Failed to retrieve ${key}:`, e);
      return null;
    }
  },

  setNumber: (key: string, value: number): void => {
    try {
      localStorage.setItem(key, value.toString());
    } catch (e) {
      console.error(`Failed to store ${key}:`, e);
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Failed to remove ${key}:`, e);
    }
  },
};

export const useAwsCredentialsPolling = () => {
  const queryClient = useQueryClient();
  const [isPolling, setIsPolling] = useState<boolean>(
    storageUtils.getBoolean(POLLING_STATE_KEY),
  );
  const maxPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);

  // Initialize polling start time from localStorage if polling is active
  useEffect(() => {
    if (isPolling && !pollingStartTimeRef.current) {
      const storedStartTime = storageUtils.getNumber(POLLING_START_TIME_KEY);

      if (storedStartTime) {
        pollingStartTimeRef.current = storedStartTime;

        // Check if we've exceeded max polling time
        if (Date.now() - storedStartTime > MAX_POLLING_TIME) {
          stopPolling();
        } else {
          // Set a new timeout for the remaining time
          const remainingTime =
            MAX_POLLING_TIME - (Date.now() - storedStartTime);
          maxPollingTimeoutRef.current = setTimeout(stopPolling, remainingTime);
        }
      }
    }
  }, [isPolling]);

  // Use React Query hook for credentials status with dynamic refetch interval
  const {
    data: credentials,
    isError,
    isLoading,
    isSuccess,
    refetch,
    error,
  } = useAwsCredentialsStatus({
    refetchOnWindowFocus: false,
    refetchInterval: isPolling ? POLLING_INTERVAL : false,
    gcTime: MAX_POLLING_TIME,
  });

  // Function to start polling
  const startPolling = () => {
    const startTime = Date.now();
    pollingStartTimeRef.current = startTime;

    // Store polling state and start time in localStorage
    storageUtils.setBoolean(POLLING_STATE_KEY, true);
    storageUtils.setNumber(POLLING_START_TIME_KEY, startTime);

    setIsPolling(true);

    // Set a timeout to stop polling after MAX_POLLING_TIME
    maxPollingTimeoutRef.current = setTimeout(stopPolling, MAX_POLLING_TIME);

    // Force an immediate refetch
    queryClient.invalidateQueries({ queryKey: ['aws-credentials-status'] });
  };

  // Function to stop polling
  const stopPolling = () => {
    setIsPolling(false);
    storageUtils.setBoolean(POLLING_STATE_KEY, false);
    storageUtils.remove(POLLING_START_TIME_KEY);

    // Clear the max polling timeout if it exists
    if (maxPollingTimeoutRef.current) {
      clearTimeout(maxPollingTimeoutRef.current);
      maxPollingTimeoutRef.current = null;
    }

    pollingStartTimeRef.current = null;
  };

  // Check for successful credentials and stop polling when complete
  useEffect(() => {
    if (isSuccess && isPolling && credentials?.hasStep1Complete) {
      stopPolling();
    }
  }, [isSuccess, credentials, isPolling]);

  // Log errors but continue polling
  useEffect(() => {
    if (isError) {
      console.error(
        'AWS Credentials polling: error fetching credentials',
        error,
      );
    }
  }, [isError, error]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (maxPollingTimeoutRef.current) {
        clearTimeout(maxPollingTimeoutRef.current);
      }
    };
  }, []);

  // Default credentials object with proper typing
  const defaultCredentials = {
    s3Arn: null,
    kmsKeyArn: null,
    snsTopicArn: null,
    lastAssumeRoleStatus: null,
    lastAssumeRoleError: null,
    hasStep1Complete: false,
  };

  return {
    credentials: credentials || defaultCredentials,
    hasStep1Complete: credentials?.hasStep1Complete || false,
    refetch,
    startPolling,
    stopPolling,
    isPolling,
    isLoading,
    isError,
    error,
  };
};
